// ─────────────────────────────────────────────────────────────────────────────
// Guest-mode session helpers.
//
// Guest mode lets cold users try tasks 1-4 of the dial_down_tension
// practice without signing up. Progress is stored in localStorage and,
// after signup, migrated to the server via /api/guest/migrate.
//
// GA4 events fire at each step of the funnel so we can measure
// activation lift and see where people drop off (default event set:
// guest_start / guest_task_completed / guest_upgrade_prompt_shown /
// guest_upgrade_signed_up / guest_upgrade_dismissed / guest_abandoned_at_task).
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "bc_guest_session_v1";

// The practice offered in guest mode + the last task index a guest may
// complete before the upgrade wall. Kept in one place so future tweaks
// (e.g. extending to 6 tasks) are a one-line change.
export const GUEST_PRACTICE_TYPE = "dial_down_tension";
export const GUEST_MAX_TASK = 4;

export const isGuestParam = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get("guest") === "1";
  } catch { return false; }
};

export const readGuestSession = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); }
  catch { return null; }
};

export const writeGuestSession = (patch) => {
  try {
    const prev = readGuestSession() || {
      practice_type: GUEST_PRACTICE_TYPE,
      started_at: new Date().toISOString(),
      task_responses: {},
      distress_levels: {},
      current_node_id: null,
      last_task_number: 0,
    };
    const next = { ...prev, ...patch, updated_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(next));
    return next;
  } catch { return null; }
};

export const clearGuestSession = () => {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

// ─── Analytics ─────────────────────────────────────────────────────────────
// Fire GA4 events via window.gtag if present. No-ops silently in the iOS
// build (analytics module skips install on Capacitor). Every event
// includes a `guest_session_id` so we can piece a funnel together for a
// single visitor across events.
const sessionId = () => {
  const s = readGuestSession();
  if (s?.session_id) return s.session_id;
  // Security-sensitive value (funnel-identifier that outlives the tab).
  // CSPRNG-backed via crypto.getRandomValues — Sonar S2245.
  const randomBytes = new Uint8Array(6);
  crypto.getRandomValues(randomBytes);
  const randomPart = Array.from(randomBytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const id = `g_${Date.now().toString(36)}_${randomPart}`;
  writeGuestSession({ session_id: id });
  return id;
};

const trackGA = (event, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, { ...params, guest_session_id: sessionId() });
    }
  } catch { /* ignore analytics failures */ }
};

export const trackGuestStart = () => trackGA("guest_start", { practice: GUEST_PRACTICE_TYPE });
export const trackGuestTaskCompleted = (taskNum) =>
  trackGA("guest_task_completed", { task_num: taskNum });
export const trackGuestUpgradePromptShown = () => trackGA("guest_upgrade_prompt_shown");
export const trackGuestUpgradeSignedUp = () => trackGA("guest_upgrade_signed_up");
export const trackGuestUpgradeDismissed = () => trackGA("guest_upgrade_dismissed");
export const trackGuestAbandonedAtTask = (taskNum) =>
  trackGA("guest_abandoned_at_task", { task_num: taskNum });

// ─── Server migration on signup ────────────────────────────────────────────
/**
 * If a guest session exists in localStorage, POST it to /api/guest/migrate
 * so the freshly-signed-up user starts inside the practice they were
 * already doing. Idempotent server-side; safe to call on every session
 * bootstrap. Non-fatal — failures don't block sign-in.
 */
export const migrateGuestSessionIfPresent = async () => {
  const s = readGuestSession();
  if (!s || !s.last_task_number) return false;
  try {
    await axios.post(`${API}/guest/migrate`, {
      practice_type: s.practice_type || GUEST_PRACTICE_TYPE,
      current_node_id: s.current_node_id,
      task_responses: s.task_responses || {},
      distress_levels: s.distress_levels || {},
      last_task_number: s.last_task_number,
    });
    clearGuestSession();
    trackGuestUpgradeSignedUp();
    return true;
  } catch {
    // Leave the session so a later attempt can retry.
    return false;
  }
};
